import { Check, X } from 'lucide-react';
import { CategoryChip } from './CategoryChip';
import { CoinIcon } from './icons';
import { RewardPill } from './RewardPill';
import type { QuestCategory, QuestState } from './types';

export interface GroupMemberRowProps {
  state: QuestState;
  category: QuestCategory;
  categoryLabel: string;
  title: string;
  /** Caption line, e.g. `Photo quest · all day`. */
  meta?: string;
  reward: number;
  rewardLabel?: string;
  /** One-line failure reason, e.g. `64/70%`. */
  failedReason?: string;
  solvedLabel?: string;
  failedLabel?: string;
  onClick?: () => void;
  className?: string;
}

/**
 * Compact 52px member row inside a GroupCard (Concept A): small category chip,
 * title (+ optional caption), then ✓ + gold amount (solved), ✗ (+ reason), or
 * the gold reward pill. Full quest detail stays one tap away.
 */
export function GroupMemberRow(props: GroupMemberRowProps) {
  const interactive = Boolean(props.onClick);
  const Tag: 'button' | 'div' = interactive ? 'button' : 'div';
  const tagProps = interactive ? ({ type: 'button', onClick: props.onClick } as const) : ({} as const);
  const dimmed = props.state === 'locked' || props.state === 'expired';

  return (
    <Tag
      {...tagProps}
      data-testid="ec-group-member"
      data-state={props.state}
      className={
        `flex min-h-[52px] w-full items-center gap-2.5 px-4 py-2 text-left ` +
        `border-t border-border/55 first:border-t-0 ` +
        `focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary ` +
        `${dimmed ? 'opacity-55' : ''} ${props.className ?? ''}`
      }
    >
      <CategoryChip
        category={props.category}
        label={props.categoryLabel}
        className="size-[30px] rounded-[9px] [&>svg]:size-3.5"
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] font-semibold text-fg">{props.title}</span>
        {props.meta && (
          <span className="block truncate text-[12.5px] font-medium text-fg-secondary">{props.meta}</span>
        )}
      </span>
      {props.state === 'solved' ? (
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <Check size={16} className="text-success" aria-hidden />
          {props.solvedLabel && <span className="sr-only">{props.solvedLabel}</span>}
          <span className="inline-flex items-center gap-1 font-display text-sm font-[650] text-bucks tabular-nums">
            <CoinIcon size={13} aria-hidden />
            +{props.reward}
          </span>
        </span>
      ) : props.state === 'failed' ? (
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <X size={16} className="text-danger" aria-hidden />
          {props.failedLabel && <span className="sr-only">{props.failedLabel}</span>}
          {props.failedReason && (
            <span className="text-[12.5px] font-medium text-fg-secondary">{props.failedReason}</span>
          )}
        </span>
      ) : (
        <RewardPill
          amount={props.reward}
          label={props.rewardLabel ?? `+${props.reward}`}
          className="px-2 py-0.5 text-[13px]"
          shimmer={props.state === 'processing'}
        />
      )}
    </Tag>
  );
}
